import React, {useContext, useEffect, useState} from "react";
import EmployeesService from "../../services/employees.service";
import Comentario from "../../models/Comentario";
import Tarea from "../../models/Tarea";
import {CBadge, CCol, CForm, CFormInput, CFormSelect, CFormTextarea, CRow} from "@coreui/react";
import BootstrapModal from "./BootstrapModal";
import {PRIORITY_OPTIONS} from "../../config/constants";
import AgendaService from "../../services/agenda.service";
import {StateContext} from "../context/StateProvider";
import Toasts from "../notificacion/Toasts";
import UsuariosService from "../../services/usuarios.service";

const TaskModal = ({ visible, onClose, onSave, task, defaultColumnId, columnOptions }) => {
    const {state, updateState} = useContext(StateContext);

    const isEdit = Boolean(task)
    const [title, setTitle] = useState(task?.title || '')
    const [description, setDescription] = useState(task?.description || '')
    const [priority, setPriority] = useState(task?.priority || 'medio')
    const [labels, setLabels] = useState(task?.labels?.join(', ') || '')
    const [assignee, setAssignee] = useState(task?.assignee || '')
    const [employees, setEmployees] = useState([])
    const [dueDate, setDueDate] = useState(task?.dueDate || '')
    const [columnId, setColumnId] = useState(task?.columnId || defaultColumnId)

    // Comentarios (nuevo)
    const [comments, setComments] = useState(task?.comments || [])
    const [commentAuthor, setCommentAuthor] = useState('')
    const [commentText, setCommentText] = useState('')

    const getEmployeeName = (id) => {
        const emp = employees.find((e) => String(e.id) === String(id))
        return emp?.nombre || 'anon'
    }

    const assigneeName = React.useMemo(() => {
        if (!assignee) return ''
        const emp = employees.find((e) => String(e.id) === String(assignee))
        return emp?.nombre || ''
    }, [employees, assignee])

    useEffect(() => {
        ;(async () => {
            try {
                const list = await UsuariosService.listarUsuariosMap()
                const safe = Array.isArray(list) ? list : []
                setEmployees(safe)
                const firstEmp = safe[0]
                // si no hay asignado, usamos el id del primer empleado
                if (!assignee && firstEmp) {
                    setAssignee(String(firstEmp.id))
                }
                // autor por defecto: nombre del primer empleado
                setCommentAuthor(firstEmp?.id || assigneeName || '')
            } catch {
                setEmployees([])
                if (!assignee) setAssignee('')
                setCommentAuthor(assigneeName || '')
            }
        })()

        if (visible) {
            setTitle(task?.title || '')
            setDescription(task?.description || '')
            setPriority(task?.priority || 'medio')
            setLabels(task?.labels?.join(', ') || '')
            setAssignee(task?.assignee || '')
            setDueDate(task?.dueDate || '')
            setColumnId(task?.columnId || defaultColumnId)
            setComments(task?.comments || [])
            setCommentAuthor(task?.assignee || '')
            setCommentText('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, task, defaultColumnId])

    const uid = () => Math.random().toString(36).slice(2, 10);

    const addComment = () => {
        if (!commentText.trim()) return;

        // El comentario se construye FUERA del try: el catch lo referencia para
        // revertir, y declararlo dentro dejaba `c` fuera de alcance (ReferenceError)
        const c = new Comentario({
            id: uid(),
            text: commentText.trim(),
            author: (commentAuthor || assignee),
            createdAt: new Date().toISOString(),
            taskId: task?.id ?? null,
        });

        (async () => {
          try {
            const created = await AgendaService.crearComentario(c)
            setComments((prev) => [created, ...prev.filter((x) => x.id !== c.id)])
            setCommentText('')
          } catch {
            setComments((prev) => prev.filter((x) => x.id !== c.id))
            const toasts = Toasts.generateToast('No se pudo crear el comentario', 'error');
            updateState({toasts: [...state.toasts, toasts]})
          }
        })()
    }

    const removeComment = (id) => {
        const prev = comments
        setComments((p) => p.filter((c) => c.id !== id));

        (async () => {
          try {
            await AgendaService.eliminarComentario(id)
          } catch {
            setComments(prev)
            const toasts = Toasts.generateToast('No se pudo eliminar el comentario', 'error');
            updateState({toasts: [...state.toasts, toasts]})
          }
        })()
    }

    return (
        <BootstrapModal
            show={visible}
            title={isEdit ? 'Editar tarea' : 'Nueva tarea'}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!title.trim()}
                        onClick={() => {
                            const payload = new Tarea({
                                ...(task || { id: uid() }),
                                title: title.trim(),
                                description: description.trim(),
                                priority,
                                labels: labels.split(',').map((s) => s.trim()).filter(Boolean),
                                assignee: assignee || null,
                                dueDate: dueDate || null,
                                columnId,
                                comments: comments,
                            })
                            onSave(/** @type {Task} */ (payload))
                        }}
                    >
                        {isEdit ? 'Guardar' : 'Crear'}
                    </button>
                </>
            }
        >
            <CForm className="d-grid gap-3">
                <CFormInput label="Título" value={title} onChange={(e) => setTitle(e.target.value)} required />
                <CFormTextarea rows={4} label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} />
                <CRow className="g-3">
                    <CCol md={6}>
                        <CFormSelect label="Prioridad" value={priority} onChange={(e) => setPriority(e.target.value)}>
                            {PRIORITY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </CFormSelect>
                    </CCol>
                    <CCol md={6}>
                        <CFormSelect label="Columna" value={columnId} onChange={(e) => setColumnId(e.target.value)}>
                            {columnOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </CFormSelect>
                    </CCol>
                </CRow>
                <CRow className="g-3">
                    <CCol md={6}>
                        <CFormInput type="date" label="Fecha vencimiento" value={dueDate || ''} onChange={(e) => setDueDate(e.target.value)} />
                    </CCol>
                    <CCol md={6}>
                        <CFormSelect label="Asignado" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                            <option value="">{employees.length ? '— Selecciona —' : 'Sin empleados'}</option>
                            {employees.map((opt) => (
                                <option key={opt.id} value={String(opt.id)}>
                                    {opt.nombre}
                                </option>
                            ))}
                        </CFormSelect>
                    </CCol>
                </CRow>


                {/* Comentarios */}
                <div className="border-top pt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fw-semibold">Comentarios</div>
                        <CBadge color="primary">{comments.length}</CBadge>
                    </div>

                    <div className="d-grid gap-2 mb-3">
                        <div className="row g-2">
                            <div className="col-md-3">
                                <CFormSelect className="form-control" value={commentAuthor} onChange={(e) => setCommentAuthor(e.target.value)}>
                                    <option value="">{employees.length ? '— Autor —' : 'Sin empleados'}</option>
                                    {employees.map((opt) => (
                                        <option key={opt.id} value={String(opt.id)}>
                                            {opt.nombre}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </div>
                            <div className="col-md-7">
                                <input
                                    className="form-control"
                                    placeholder="Escribe un comentario"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                />
                            </div>
                            <div className="col-md-2 d-grid">
                                <button type="button" className="btn btn-outline-primary" onClick={addComment} disabled={!commentText.trim()}>
                                    Añadir
                                </button>
                            </div>
                        </div>
                    </div>

                    {comments.length === 0 ? (
                        <div className="text-muted small">Sin comentarios todavía.</div>
                    ) : (
                        <div className="list-group">
                            {comments.map((c) => (
                                <div key={c.id} className="list-group-item d-flex flex-column gap-1">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="small text-muted">
                                            <strong>{getEmployeeName(c.author)}</strong> · {new Date(c.createdAt).toLocaleString()}
                                        </div>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => removeComment(c.id)}>
                                            Eliminar
                                        </button>
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CForm>
        </BootstrapModal>
    )
}

export default TaskModal;